import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import historyProjectBg from "../assets/history-project-bg.png";
import historyLeftBg from "../assets/history-left-bg.jpg";
import historySideLogo from "../assets/history-side-logo.png";

const http = Axios.create({
  baseURL: "https://backend.sansfrontieres-design.com",
  withCredentials: false,
});

// Permite que el endpoint devuelva JSON puro (o texto) sin romper
function safeParseJson(text) {
  if (text == null) return [];
  let s = String(text);
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1); // BOM
  const i = s.search(/[\[{]/);
  if (i === -1) return [];
  const chunk = s.slice(i).trim();
  try {
    const j = JSON.parse(chunk);
    return Array.isArray(j) ? j : Array.isArray(j?.data) ? j.data : [];
  } catch {
    return [];
  }
}

// Dedup por snapshot (created_at si existe; si no, jobdatesub)
function dedupeBySnapshotKey(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = String(r?.created_at || r?.jobdatesub || "").trim();
    if (!key) {
      out.push(r);
      continue;
    }
    if (!seen.has(key)) {
      seen.add(key);
      out.push(r);
    }
  }
  return out;
}

function normStatus(s) {
  return String(s || "").trim().toLowerCase();
}

// ============================================================
// COLUMN WIDTHS - EDIT ONLY THIS SECTION IF YOU WANT TO RESIZE
// Values are in pixels. Example: joblob: 120
// ============================================================
const COLUMN_WIDTHS = {
  jobid: 110,
  jobsta: 90,
  jobuser: 90,
  jobti: 155,
  jobdatesub: 145,
  jobpro: 90,
  jobcomty: 95,      // shown as jobcounty
  joblob: 120,
  "his-project": 90, // shown as hisproject
  created_at: 165,
  jobnotesadmin: 220,
  jobnotes: 220,
  jobrouteaerial: 135,
  jobrouteug: 125,
  jobcoaxae: 120,
  jobcoaxug: 120,
  jobasbae: 120,
  jobasbug: 120,
  joblode: 100,
  jobhc: 90,
  jobproname: 190,
  jobproport: 125,
  jobou: 100,
};

// ============================================================
// COLUMN HEADER NAMES - EDIT ONLY THE "label" TEXT TO RENAME
// Example: { key: "jobid", label: "JOB ID" }
// IMPORTANT: do not change "key" unless the DB field itself changes.
// ============================================================
const HISTORY_COLUMNS = [
  { key: "jobid", label: "JOB ID" },
  { key: "jobsta", label: "STATUS" },
  { key: "jobuser", label: "USER" },
  { key: "jobti", label: "TYPE" },
  { key: "jobdatesub", label: "DATE LAST" },
  { key: "jobpro", label: "PROJECT" },
  // En BD el campo está guardado como jobcomty; en pantalla lo mostramos como jobcounty.
  { key: "jobcomty", label: "COUNTY" },
  { key: "joblob", label: "LOB" },
  { key: "his-project", label: "HIST-PROJ" },
  { key: "created_at", label: "CREATED AT" },
  { key: "jobnotesadmin", label: "ADMIN NOTES" },
  { key: "jobnotes", label: "JOB NOTES" },
  { key: "jobrouteaerial", label: "AERIAL" },
  { key: "jobrouteug", label: "UG" },
  { key: "jobcoaxae", label: "COAX AE" },
  { key: "jobcoaxug", label: "COAX UG" },
  { key: "jobasbae", label: "ASB AE" },
  { key: "jobasbug", label: "ASB UG" },
  { key: "joblode", label: "LODE" },
  { key: "jobhc", label: "HC" },
  { key: "jobproname", label: "PROJECT NAME" },
  { key: "jobproport", label: "COM" },
  { key: "jobou", label: "" }
];

// ============================================================
// SECONDARY SORT - AUTOMATIC TIE BREAKER
// Keep created_at DESC as the second criterion for header sorting.
// ============================================================
const SECONDARY_SORT = {
  key: "created_at",
  direction: "desc",
};

export default function HistoryOk() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Estado actual (tabla projects)
  const [currentStatus, setCurrentStatus] = useState("");
  const [jobExists, setJobExists] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });
  const [selectedRows, setSelectedRows] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("history:selectedRows") || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  const [favoriteRows, setFavoriteRows] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("history:favoriteRows") || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  const searchSeq = useRef(0);

  const rowsToShow = useMemo(() => {
    const arr = Array.isArray(rows) ? [...rows] : [];
    arr.sort((a, b) =>
      String(b?.created_at || b?.jobdatesub || "").localeCompare(
        String(a?.created_at || a?.jobdatesub || "")
      )
    );
    return dedupeBySnapshotKey(arr);
  }, [rows]);

  const latestRowsByJob = useMemo(() => {
    // rowsToShow ya viene ordenado por la actualización más reciente.
    // Conservamos solo la última actualización de cada Job ID para la tabla principal.
    const seenJobs = new Set();
    const latest = [];
    for (const row of rowsToShow) {
      const jobid = String(row?.jobid || "").trim();
      const key = jobid || `__blank_${latest.length}`;
      if (seenJobs.has(key)) continue;
      seenJobs.add(key);
      latest.push(row);
    }

    // Sin búsqueda mostramos los 250 Jobs únicos más recientemente actualizados.
    const visible = submittedQ.trim() ? latest : latest.slice(0, 250);

    // Sin un encabezado seleccionado conservamos el orden por defecto:
    // última actualización primero (created_at/jobdatesub DESC).
    if (!sortConfig.key) return visible;

    const direction = sortConfig.direction === "asc" ? 1 : -1;
    const key = sortConfig.key;

    const comparePrimary = (a, b) => {
      const avRaw = a?.[key];
      const bvRaw = b?.[key];
      const av = avRaw == null ? "" : String(avRaw).trim();
      const bv = bvRaw == null ? "" : String(bvRaw).trim();

      const an = Number(av.replace(/,/g, ""));
      const bn = Number(bv.replace(/,/g, ""));
      const bothNumeric = av !== "" && bv !== "" && Number.isFinite(an) && Number.isFinite(bn);
      if (bothNumeric) return (an - bn) * direction;

      return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * direction;
    };

    const compareSecondary = (a, b) => {
      const secondaryDirection = SECONDARY_SORT.direction === "asc" ? 1 : -1;
      const av = String(a?.[SECONDARY_SORT.key] || a?.jobdatesub || "").trim();
      const bv = String(b?.[SECONDARY_SORT.key] || b?.jobdatesub || "").trim();
      return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * secondaryDirection;
    };

    return [...visible].sort((a, b) => {
      const primaryResult = comparePrimary(a, b);
      if (primaryResult !== 0) return primaryResult;
      return compareSecondary(a, b);
    });
  }, [rowsToShow, submittedQ, sortConfig]);

  const sortByColumn = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const resetTableOrder = () => {
    setSortConfig({ key: null, direction: "desc" });
  };

  useEffect(() => {
    try { localStorage.setItem("history:selectedRows", JSON.stringify(selectedRows)); } catch {}
  }, [selectedRows]);

  useEffect(() => {
    try { localStorage.setItem("history:favoriteRows", JSON.stringify(favoriteRows)); } catch {}
  }, [favoriteRows]);

  const handleCheckboxChange = (e, jobId) => {
    const id = String(jobId ?? "").trim();
    if (!id) return;
    setSelectedRows((prev) =>
      e.target.checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)
    );
  };

  const toggleFavorite = (jobId) => {
    const id = String(jobId ?? "").trim();
    if (!id) return;
    setFavoriteRows((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const canUnarchive = useMemo(() => {
    if (!jobExists) return false;
    const s = normStatus(currentStatus);
    return s === "archived" || s === "payed" || s === "billing";
  }, [jobExists, currentStatus]);

  useEffect(() => {
    if (!detailsOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDetailsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [detailsOpen]);

  const fetchCurrentStatusFromProjects = async (jobid) => {
    const { data } = await http.get("/get_projects.php", { params: { _: Date.now() } });
    const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
    const jid = String(jobid || "").trim();
    const row = list.find((r) => String(r?.jobid || "").trim() === jid);

    if (!row) {
      setJobExists(false);
      setCurrentStatus("");
      return;
    }
    setJobExists(true);
    setCurrentStatus(row?.jobsta || "");
  };

  const loadHistory = async (searchText = "", options = {}) => {
    const jid = String(searchText || "").trim();
    const seq = ++searchSeq.current;
    const isSearch = jid.length > 0;

    setLoading(true);
    setError("");
    if (options.closeDetails !== false) setDetailsOpen(false);

    try {
      const { data: raw } = await http.get("/getHistoryPreview.php", {
        params: isSearch
          ? { q: jid, like: 1, limit: 250, _: Date.now() }
          : { limit: 10000, _: Date.now() },
        responseType: "text",
        transformResponse: [(d) => d],
      });
      const list = safeParseJson(raw);
      if (seq !== searchSeq.current) return;
      setRows(list);

      // Solo consultamos el status actual cuando el texto coincide exactamente con un Job ID.
      if (isSearch && list.some((r) => String(r?.jobid || "").trim() === jid)) {
        await fetchCurrentStatusFromProjects(jid);
      } else {
        setJobExists(false);
        setCurrentStatus("");
      }
    } catch (e) {
      if (seq !== searchSeq.current) return;
      // eslint-disable-next-line no-console
      console.error("History fetch error:", e);
      setRows([]);
      setJobExists(false);
      setCurrentStatus("");
      setError("No se pudo cargar el historial. Revisa conexión o endpoint.");
    } finally {
      if (seq === searchSeq.current) setLoading(false);
    }
  };

  const buscar = async () => {
    const jid = q.trim();
    setSubmittedQ(jid);
    await loadHistory(jid, { closeDetails: true });
  };

  const clearSearch = async () => {
    setQ("");
    setSubmittedQ("");
    setJobExists(false);
    setCurrentStatus("");
    setDetailsOpen(false);
    await loadHistory("", { closeDetails: true });
  };

  // Al entrar: cargar suficientes registros para mostrar por default hasta 250 Jobs únicos.
  // Escribir en el campo NO dispara ninguna búsqueda; solo Buscar o Enter.
  useEffect(() => {
    loadHistory("", { closeDetails: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unarchive = async () => {
    const jid = submittedQ.trim();
    if (!jid || !canUnarchive) return;

    setUnarchiving(true);
    setError("");
    try {
      // Preferimos update_project_row.php; fallback a update_project.php
      let ok = false;

      try {
        const res1 = await http.post(
          "/update_project_row.php",
          { jobid: jid, fields: { jobsta: "Pre-Archived" } },
          { headers: { "Content-Type": "application/json" } }
        );
        if (res1?.data?.status === "success") ok = true;
      } catch (e) {
        // ignore
      }

      if (!ok) {
        const res2 = await http.post(
          "/update_project.php",
          { jobid: jid, jobsta: "Pre-Archived" },
          { headers: { "Content-Type": "application/json" } }
        );
        if (res2?.data?.status !== "success") {
          throw new Error(res2?.data?.message || "No se pudo actualizar status");
        }
      }

      await buscar();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Unarchive error:", e);
      setError(e?.message || "No se pudo hacer Unarchive.");
    } finally {
      setUnarchiving(false);
    }
  };

  const exactSearchRows = useMemo(() => {
    const jid = submittedQ.trim();
    if (!jid) return [];
    return rowsToShow.filter((r) => String(r?.jobid || "").trim() === jid);
  }, [submittedQ, rowsToShow]);

  const projectSummary = useMemo(() => {
    if (!exactSearchRows.length) return null;
    return exactSearchRows[0];
  }, [exactSearchRows]);

  const analytics = useMemo(() => {
    if (!exactSearchRows.length) {
      return {
        total: 0,
        statusCounts: [],
        userCounts: [],
        firstActivity: "—",
        lastActivity: "—",
        corrections: 0,
        inHold: 0,
        readyQc: 0,
        donut: "conic-gradient(#e5e7eb 0 100%)",
      };
    }

    const statusMap = new Map();
    const userMap = new Map();
    const dated = [];
    exactSearchRows.forEach((r) => {
      const st = String(r?.jobsta || "").trim() || "(blank)";
      statusMap.set(st, (statusMap.get(st) || 0) + 1);
      const user = String(r?.jobuser || "").trim() || "Unknown";
      userMap.set(user, (userMap.get(user) || 0) + 1);
      const d = String(r?.created_at || r?.jobdatesub || "").trim();
      if (d) dated.push(d);
    });

    const statusCounts = [...statusMap.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    const userCounts = [...userMap.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const palette = ["#2b88a3", "#68b8c8", "#7fb46d", "#d7a15a", "#756bc7", "#8c8c8c", "#b76d87", "#4b9f82"];
    let cursor = 0;
    const parts = statusCounts.map((item, idx) => {
      const start = cursor;
      cursor += (item.count / exactSearchRows.length) * 100;
      return `${palette[idx % palette.length]} ${start}% ${cursor}%`;
    });

    const statusLowerCount = (name) =>
      exactSearchRows.filter((r) => normStatus(r?.jobsta) === name).length;

    dated.sort((a, b) => a.localeCompare(b));
    return {
      total: exactSearchRows.length,
      statusCounts,
      userCounts,
      firstActivity: dated[0] || "—",
      lastActivity: dated[dated.length - 1] || "—",
      corrections: statusLowerCount("correction"),
      inHold: statusLowerCount("in hold"),
      readyQc: statusLowerCount("ready for qc"),
      donut: `conic-gradient(${parts.join(",")})`,
      palette,
    };
  }, [exactSearchRows]);

  const summaryField = (key, fallback = "—") => {
    const value = projectSummary?.[key];
    if (value === null || value === undefined || String(value).trim() === "") return fallback;
    return String(value);
  };

  return (
    <div className="history-shell">
      <div className="container py-4 history-page">
        <style>{`
          :root{
            --sf-bg:#15191f;
            --sf-card:#1b2027;
            --sf-ink:#eef2f6;
            --sf-muted:#9ca6b1;
            --sf-border:#343b45;
            --sf-head:#20262d;
            --sf-row:#1a1f25;
            --sf-green:#7fd3b2;   /* pastel green */
            --sf-green2:#59c59e;  /* hover */
            --sf-greenText:#0f3d2e;
            --sf-shadow:0 10px 25px rgba(33,24,16,.08);
          }
          body{background:var(--sf-bg);}
          .history-shell{
            min-height:100vh;
            background:#14181e;
            color:var(--sf-ink);
          }
          .history-page{max-width:none!important;width:100%;height:100vh;padding:10px!important;}
          .history-grid{display:grid;grid-template-columns:330px minmax(0,1fr);gap:16px;height:100%;min-height:0;}
          .glass-panel{background:rgba(19,24,30,.70);border:1px solid rgba(255,255,255,.08);box-shadow:0 18px 45px rgba(0,0,0,.24),inset 0 1px 0 rgba(255,255,255,.05);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);}
          .history-sidebar{position:relative;border-radius:0;padding:12px;display:flex;flex-direction:column;gap:7px;min-height:0;overflow:hidden;background-image:linear-gradient(180deg,rgba(11,15,20,.66),rgba(11,15,20,.76)),url("${historyLeftBg}");background-size:cover;background-position:center;border:1px solid #2d343d;isolation:isolate;}
          .brand-card{display:flex;align-items:center;justify-content:flex-start;gap:10px;padding:8px 6px 10px;border-bottom:1px solid rgba(255,255,255,.42);background:rgba(244,246,248,.78);backdrop-filter:blur(7px);-webkit-backdrop-filter:blur(7px);}
          .brand-logo{width:200px;max-width:100%;height:auto;object-fit:contain;object-position:left center;border-radius:0;mix-blend-mode:normal;filter:none;display:block;}
          .brand-name{font-size:16px;line-height:1.02;font-weight:700;letter-spacing:.02em;color:#f0f3f7;}
          .brand-sub{font-size:8px;letter-spacing:.22em;color:#aab3bc;margin-top:4px;text-transform:uppercase;}
          .sidebar-card{background:rgba(190,198,208,.16);border:1px solid rgba(230,236,242,.18);box-shadow:inset 0 1px 0 rgba(255,255,255,.05);border-radius:0;padding:9px 10px;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);}
          .sidebar-results{flex:1;min-height:0;display:grid;grid-template-rows:minmax(0,1.65fr) minmax(0,1.05fr) minmax(0,.72fr) auto;gap:7px;}
          .sidebar-results > .sidebar-card{height:100%;display:flex;flex-direction:column;justify-content:flex-start;}
          .sidebar-results > .sidebar-card:first-child{padding-top:12px;padding-bottom:12px;}
          .sidebar-results .project-info-grid{align-content:start;}
          .sidebar-results .donut-row{flex:1;align-items:center;}
          .sidebar-results .activity-list{flex:1;justify-content:center;}

          .side-title{font-size:13.5px;letter-spacing:.10em;font-weight:800;color:#eef2f6;margin-bottom:6px;text-transform:uppercase;}
          .project-info-grid{display:grid;grid-template-columns:88px 1fr;column-gap:7px;row-gap:4px;font-size:13.5px;line-height:1.12;}
          .project-info-grid .k{color:#97a1ab;white-space:nowrap;}
          .project-info-grid .v{color:#eef2f6;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
          .mini-status{display:inline-flex;align-items:center;padding:2px 7px;border-radius:999px;background:rgba(94,172,130,.18);border:1px solid rgba(94,172,130,.34);color:#bde8cf;font-weight:700;}
          .empty-analytics{position:relative;overflow:hidden;flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;color:#f7f4f0;padding:18px;width:100%;background:rgba(190,198,208,.12);isolation:isolate;}
          .empty-analytics:before{content:"";position:absolute;inset:0;border-radius:0;background:linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.01));border:1px solid rgba(255,255,255,.10);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);pointer-events:none;}
          .empty-analytics:after{content:"";position:absolute;inset:0;background:radial-gradient(circle at 18% 22%, rgba(117,213,228,.20), transparent 28%), radial-gradient(circle at 82% 14%, rgba(214,184,154,.20), transparent 28%);pointer-events:none;z-index:0;}
          .empty-analytics > *{position:relative;z-index:2;}
          .empty-watermark{position:absolute;inset:auto 14px 14px auto;font-size:34px;font-weight:800;letter-spacing:.02em;color:rgba(255,255,255,.12);pointer-events:none;z-index:1;}
          .empty-orb{position:absolute;border-radius:50%;filter:blur(18px);opacity:.55;pointer-events:none;z-index:1;mix-blend-mode:screen;}
          .empty-orb.orb1{width:118px;height:118px;left:-18px;top:26px;background:rgba(106,198,216,.34);}
          .empty-orb.orb2{width:136px;height:136px;right:-28px;bottom:38px;background:rgba(216,191,166,.34);}
          .empty-overlay-card{width:100%;max-width:240px;border-radius:0;padding:16px 13px;background:rgba(192,200,210,.18);border:1px solid rgba(236,241,245,.20);box-shadow:0 10px 28px rgba(0,0,0,.16);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
          .empty-badge-row{display:flex;gap:8px;flex-wrap:wrap;justify-content:center;margin-top:14px;}
          .empty-badge{font-size:9px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:5px 9px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);color:#ffffff;box-shadow:0 4px 10px rgba(52,46,40,.05);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);}
          .empty-icon{width:54px;height:54px;border-radius:50%;display:grid;place-items:center;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.24);font-size:23px;margin:0 auto 12px;box-shadow:0 10px 24px rgba(19,21,26,.14);}
          .donut-row{display:flex;align-items:center;gap:10px;}
          .donut{width:88px;height:88px;border-radius:50%;position:relative;flex:0 0 auto;box-shadow:inset 0 0 0 1px rgba(0,0,0,.04);}
          .donut:after{content:"";position:absolute;inset:18px;background:rgba(24,29,36,.97);border-radius:50%;box-shadow:0 2px 9px rgba(0,0,0,.18);}
          .donut-center{position:absolute;inset:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;font-weight:700;font-size:18px;color:#f2f5f8;}
          .donut-center small{font-size:9px;color:#98a3ad;font-weight:600;letter-spacing:.03em;}
          .legend{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px;}
          .legend-row{display:grid;grid-template-columns:7px minmax(0,1fr) auto;gap:5px;align-items:center;font-size:13.5px;color:#d1d7de;line-height:1.05;}
          .legend-dot{width:8px;height:8px;border-radius:2px;}
          .legend-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
          .activity-list{display:flex;flex-direction:column;gap:5px;}
          .activity-row{display:grid;grid-template-columns:64px minmax(0,1fr) 30px;gap:6px;align-items:center;font-size:13.5px;}
          .activity-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#d1d7de;}
          .activity-track{height:7px;border-radius:99px;background:rgba(255,255,255,.10);overflow:hidden;}
          .activity-fill{height:100%;border-radius:99px;background:linear-gradient(90deg,#2d829e,#69b7c7);}
          .activity-num{text-align:right;color:#b1bac3;}
          .stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:5px;}
          .stat-card{min-height:46px;padding:6px 7px;border-radius:0;background:rgba(190,198,208,.16);border:1px solid rgba(230,236,242,.16);display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);}
          .stat-value{font-size:13.5px;font-weight:800;color:#eef2f6;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
          .stat-label{font-size:13.5px;color:#a7b0ba;line-height:1.1;margin-top:2px;}
          .history-right{display:grid;grid-template-rows:auto minmax(0,1fr);gap:12px;min-width:0;min-height:0;}
          .history-top-area{position:relative;border-radius:0;padding:12px;overflow:hidden;background-image:linear-gradient(90deg, rgba(31,35,41,.30), rgba(31,35,41,.12) 52%, rgba(255,255,255,.05)), url("${historyProjectBg}");background-size:cover;background-position:center 58%;box-shadow:inset 0 1px 0 rgba(255,255,255,.18);}
          .history-hero{position:relative;min-height:140px;border-radius:0;padding:18px 20px;display:flex;align-items:flex-start;gap:12px;margin-bottom:12px;background:transparent;box-shadow:none;overflow:hidden;}
          .history-hero:after{content:"";position:absolute;inset:0;background:transparent;pointer-events:none;}
          .top-title-row{position:relative;z-index:2;display:flex;align-items:flex-start;gap:12px;width:100%;}
          .history-title{font-size:32px;font-weight:700;color:#ffffff;letter-spacing:-.7px;margin:0;text-shadow:0 2px 18px rgba(0,0,0,.22);}
          .history-subtitle{font-size:11px;color:rgba(255,255,255,.88);margin-top:4px;text-shadow:0 1px 10px rgba(0,0,0,.18);}
          .top-actions{margin-left:auto;display:flex;align-items:center;gap:8px;position:relative;z-index:2;}
          .sf-toolbar{display:grid;grid-template-columns:minmax(190px,1fr) auto 38px auto minmax(150px,180px);gap:8px;align-items:center;background:rgba(255,255,255,.72);border:1px solid rgba(255,255,255,.88);border-radius:18px;padding:12px;box-shadow:0 8px 22px rgba(49,42,36,.08);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
          .search-input-wrap{position:relative;min-width:0;}
          .sf-input{border-radius:12px;border:1px solid rgba(84,77,70,.14);background:rgba(255,255,255,.72);padding:10px 38px 10px 12px;}
          .sf-input:focus{border-color:#4b9bb0;box-shadow:0 0 0 .22rem rgba(66,151,176,.16);background:#fff;}
          .search-clear{position:absolute;right:10px;top:50%;transform:translateY(-50%);width:24px;height:24px;border:0;background:transparent;color:#59616a;font-size:20px;line-height:1;display:grid;place-items:center;padding:0;cursor:pointer;border-radius:50%;font-weight:500;}
          .search-clear:hover{background:rgba(39,48,57,.10);color:#1f2933;}
          .btn-accent{background:linear-gradient(180deg,#3e94ad,#267c98);border-color:#2f829d;color:#fff;border-radius:12px;font-weight:600;box-shadow:0 5px 12px rgba(43,128,154,.18);}
          .btn-accent:hover{background:linear-gradient(180deg,#48a0b8,#2c86a2);color:#fff;}
          .btn-soft{border-radius:12px;font-weight:600;border:1px solid rgba(77,70,64,.10);background:rgba(255,255,255,.58);color:#37322e;}
          .btn-soft:hover{background:rgba(255,255,255,.82);}
          .btn-unarchive-on{background:rgba(255,255,255,.68);border:1px solid rgba(65,58,53,.16);color:#413b36;border-radius:12px;font-weight:600;}
          .btn-unarchive-off{background:rgba(255,255,255,.38);border:1px solid rgba(65,58,53,.10);color:#9a938d;border-radius:12px;font-weight:600;}
          .pill{background:rgba(117,204,168,.24);color:#245e48;border:1px solid rgba(68,164,124,.20);border-radius:999px;padding:6px 10px;font-size:11px;font-weight:700;white-space:nowrap;}
          .current-card{height:38px;min-height:38px;border-radius:12px;padding:4px 8px;background:rgba(255,255,255,.55);border:1px solid rgba(255,255,255,.82);display:flex;flex-direction:row;align-items:center;justify-content:center;gap:7px;min-width:0;}
          .current-label{font-size:8px;letter-spacing:.05em;color:#7b746d;text-transform:uppercase;white-space:nowrap;}
          .status-pill{margin-top:0;display:inline-flex;width:max-content;background:rgba(121,207,167,.24);color:#226447;border:1px solid rgba(73,166,127,.28);border-radius:999px;padding:3px 8px;font-size:10px;font-weight:700;max-width:170px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
          .history-table-area{display:flex;flex-direction:column;min-height:0;border-radius:0;overflow:hidden;background:#1a1f25;border:1px solid #343b45;box-shadow:none;}
          .table-wrap{position:relative;flex:1 1 auto;min-height:0;overflow:auto;}
          thead.sticky{position:sticky;top:0;z-index:3;box-shadow:0 1px 0 rgba(0,0,0,.08);}
          .sf-thead{background:#242a31!important;color:#f4f7fa!important;}
          .sf-thead th{position:sticky;top:0;z-index:8;font-weight:800;color:#8fe5e2!important;background:linear-gradient(180deg,#34383b 0%,#25282b 100%)!important;padding:10px 12px;white-space:nowrap;font-size:11.5px;text-align:center!important;vertical-align:middle!important;letter-spacing:.02em;border-right:1px solid #4a5056!important;border-bottom:1px solid #596168!important;cursor:pointer;user-select:none;}
          .sf-thead th:hover{background:linear-gradient(180deg,#3d4448 0%,#2c3033 100%)!important;color:#b5fffb!important;}
          .sort-arrow{display:inline-block;margin-left:5px;font-size:9px;opacity:.95;vertical-align:1px;}
          .btn-order{width:38px;height:38px;padding:0!important;border-radius:12px;font-weight:800;border:1px solid #f0b88f;background:#FFDAC1;color:#5a3a24;display:grid;place-items:center;font-size:19px;line-height:1;white-space:nowrap;}
          .btn-order:hover{background:#ffd0b1;color:#3f291a;}
          .sf-table{font-size:12.65px;--bs-table-bg:transparent;--bs-table-color:#e3e8ed;--bs-table-striped-bg:transparent;--bs-table-hover-bg:transparent;}
          .sf-table td{padding:4px 10px;border-bottom:1px solid #303740;border-right:1px solid #2b323a;background:#1b2027;color:#e3e8ed;}
          .history-main-table tbody tr{height:31px;}
          .history-main-table td{height:31px;vertical-align:middle;}
          .history-main-table .main-cell-content{height:22px;max-height:22px;overflow:hidden;display:block;white-space:nowrap;text-overflow:ellipsis;line-height:22px;width:100%;min-width:0;max-width:100%;}
          .history-main-table th,.history-main-table td{box-sizing:border-box;overflow:hidden;}
          /* Row marker + favorite columns (same interaction as GridPage) */
          .history-main-table .marker-head{width:44px!important;min-width:44px!important;max-width:44px!important;padding-left:4px!important;padding-right:4px!important;cursor:default;}
          .history-main-table td.sel-cell,.history-main-table td.fav-cell{position:relative;width:44px!important;min-width:44px!important;max-width:44px!important;padding:0 4px!important;text-align:center;overflow:hidden;}
          .row-index{display:inline-block;min-width:20px;opacity:.9;color:#aeb8c2;font-size:11px;}
          .row-check{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);opacity:0;pointer-events:none;transition:opacity .15s ease;cursor:pointer;}
          td.sel-cell:hover .row-check{opacity:1;pointer-events:auto;}
          td.sel-cell:hover .row-index{opacity:0;}
          .fav-star{cursor:pointer;font-size:20px;line-height:1;color:#ff9800;transition:transform .12s ease,opacity .12s ease;user-select:none;}
          .fav-star.off{opacity:.85;}
          .fav-star:hover{transform:scale(1.1);}
          .history-main-table tbody tr.row-selected td{background:#344b43!important;}
          .history-main-table tbody tr.row-selected:hover td{background:#3b564d!important;}
          .history-main-table tbody tr.row-selected td.sel-cell .row-check{opacity:1;pointer-events:auto;}
          .history-main-table tbody tr.row-selected td.sel-cell .row-index{opacity:0;}
          .sf-table tbody tr:nth-child(even) td{background:#20262d;}
          .sf-table tbody tr:hover td{background:#29313a;}
          .cell-wrap{white-space:pre-wrap;word-break:break-word;max-width:520px;}
          .history-main-table{min-width:3200px;width:max-content;height:auto;}
          .table-wrap > .table-responsive{overflow:visible;min-width:max-content;}
          .table-footer{padding:8px 12px;border-top:1px solid #343b45;font-size:10.35px;color:#9ca6b1;background:#181d23;}
          .btn-details-on{background:linear-gradient(180deg,#36322f,#272421);border-color:#2d2926;color:#fff;border-radius:12px;font-weight:600;box-shadow:0 5px 12px rgba(0,0,0,.12);}
          .btn-details-on:hover{background:#46403b;color:#fff;}
          .btn-details-off{background:rgba(255,255,255,.38);border-color:rgba(65,58,53,.10);color:#9a938d;border-radius:12px;font-weight:600;}
          .sidebar-bottom-actions{margin-top:auto;padding-top:6px;border-top:1px solid rgba(230,236,242,.14);}
          .sidebar-unarchive{width:100%;border-radius:0!important;min-height:32px;text-transform:uppercase;letter-spacing:.08em;font-size:13.5px;padding:6px 10px;}
          .history-sidebar:before{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(8,11,15,.18),rgba(8,11,15,.34));z-index:-1;pointer-events:none;}
          @media (max-height:760px){.history-sidebar{padding:9px;gap:5px}.brand-logo{width:46px;height:auto}.brand-name{font-size:14px}.brand-sub{font-size:7px}.sidebar-card{padding:7px 8px}.project-info-grid{row-gap:3px;font-size:13.5px}.donut{width:76px;height:76px}.donut:after{inset:16px}.donut-center{font-size:16px}.stat-card{min-height:40px;padding:5px}.sidebar-unarchive{min-height:28px;padding:5px 8px}.sidebar-results{gap:5px}}

          .details-overlay{position:fixed;inset:0;z-index:9999;background:rgba(38,34,30,.30);backdrop-filter:blur(7px);-webkit-backdrop-filter:blur(7px);padding:34px;display:flex;align-items:center;justify-content:center;}
          .details-window{width:min(1500px,96vw);height:min(860px,92vh);background:var(--sf-bg);border:1px solid var(--sf-border);border-radius:18px;box-shadow:0 24px 70px rgba(0,0,0,.34);display:flex;flex-direction:column;overflow:hidden;}
          .details-head{display:flex;align-items:center;gap:12px;padding:16px 18px;background:var(--sf-card);border-bottom:1px solid var(--sf-border);}
          .details-title{font-size:22px;font-weight:500;color:var(--sf-ink);margin:0;}
          .details-subtitle{font-size:12px;color:var(--sf-muted);margin-top:2px;}
          .details-close{margin-left:auto;border:1px solid var(--sf-border);background:var(--sf-head);color:var(--sf-ink);border-radius:12px;padding:8px 14px;font-weight:500;}
          .details-close:hover{background:var(--sf-row);}
          .details-body{padding:18px;overflow:auto;flex:1;}
          .details-table-card{background:var(--sf-card);border:1px solid var(--sf-border);border-radius:16px;overflow:hidden;box-shadow:var(--sf-shadow);}
          @media (max-width:1180px){.history-grid{grid-template-columns:270px minmax(0,1fr)}.sf-toolbar{grid-template-columns:minmax(160px,1fr) auto 38px auto minmax(135px,160px);gap:6px}.current-card{display:flex}}
          @media (max-width:900px){.history-page{height:auto;min-height:100vh}.history-grid{grid-template-columns:1fr;height:auto}.history-sidebar{max-height:none}.history-right{min-height:680px}.history-hero{min-height:120px;}}
          @media (max-width:700px){.details-overlay{padding:10px}.details-window{width:100%;height:96vh;border-radius:14px}.details-head{padding:12px}.details-body{padding:10px}}
        `}</style>

        <div className="history-grid">
          <aside className="history-sidebar glass-panel">
            <div className="brand-card">
              <img src={historySideLogo} alt="Sans Frontières Design" className="brand-logo" />
              
            </div>

            {!projectSummary ? (
              <div className="empty-analytics">
                <span className="empty-orb orb1" />
                <span className="empty-orb orb2" />
                <div className="empty-overlay-card">
                  <div className="empty-icon">⌕</div>
                  <div style={{fontWeight:700,color:'#ffffff',marginBottom:6}}>Project analytics</div>
                  <div style={{fontSize:10,lineHeight:1.5,maxWidth:215,color:'rgba(255,255,255,.88)'}}>Search a Job ID to view project information, status activity and history statistics.</div>
                  <div className="empty-badge-row">
                    <span className="empty-badge">Project info</span>
                    <span className="empty-badge">Status analytics</span>
                    <span className="empty-badge">User activity</span>
                  </div>
                </div>
                <div className="empty-watermark">SFD</div>
              </div>
            ) : (
              <div className="sidebar-results">
                <div className="sidebar-card">
                  <div className="side-title">Project Info</div>
                  <div className="project-info-grid">
                    <div className="k">Job ID</div><div className="v">{summaryField("jobid")}</div>
                    <div className="k">Current Status</div><div className="v"><span className="mini-status">{jobExists ? (currentStatus || summaryField("jobsta")) : summaryField("jobsta")}</span></div>
                    <div className="k">User</div><div className="v">{summaryField("jobuser")}</div>
                    <div className="k">Type</div><div className="v" title={summaryField("jobti")}>{summaryField("jobti")}</div>
                    <div className="k">Project</div><div className="v">{summaryField("jobpro")}</div>
                    <div className="k">EP Status</div><div className="v">{summaryField("jobclo")}</div>
                    <div className="k">Project Name</div><div className="v" title={summaryField("jobproname")}>{summaryField("jobproname")}</div>
                    <div className="k">OU</div><div className="v">{summaryField("jobou")}</div>
                    <div className="k">LOB</div><div className="v">{summaryField("joblob")}</div>
                    <div className="k">HC</div><div className="v">{summaryField("jobhc")}</div>
                    <div className="k">LODE</div><div className="v">{summaryField("joblode")}</div>
                  </div>
                </div>

                <div className="sidebar-card">
                  <div className="side-title">Status Changes</div>
                  <div className="donut-row">
                    <div className="donut" style={{background: analytics.donut}}>
                      <div className="donut-center">{analytics.total}<small>events</small></div>
                    </div>
                    <div className="legend">
                      {analytics.statusCounts.slice(0,6).map((item,idx)=>(
                        <div className="legend-row" key={item.name}>
                          <span className="legend-dot" style={{background:analytics.palette[idx % analytics.palette.length]}} />
                          <span className="legend-name" title={item.name}>{item.name}</span>
                          <span>{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="sidebar-card">
                  <div className="side-title">User Activity</div>
                  <div className="activity-list">
                    {analytics.userCounts.slice(0,5).map((item)=>(
                      <div className="activity-row" key={item.name}>
                        <span className="activity-name" title={item.name}>{item.name}</span>
                        <span className="activity-track"><span className="activity-fill" style={{width:`${Math.max(4,(item.count/analytics.total)*100)}%`,display:'block'}} /></span>
                        <span className="activity-num">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="stats-grid">
                  <div className="stat-card"><div className="stat-value">{analytics.total}</div><div className="stat-label">Status events</div></div>
                  <div className="stat-card"><div className="stat-value">{analytics.corrections}</div><div className="stat-label">Corrections</div></div>
                  <div className="stat-card"><div className="stat-value">{analytics.inHold}</div><div className="stat-label">In Hold</div></div>
                  <div className="stat-card"><div className="stat-value">{analytics.readyQc}</div><div className="stat-label">Ready for QC</div></div>
                  <div className="stat-card"><div className="stat-value" title={analytics.firstActivity}>{analytics.firstActivity.split(' ')[0]}</div><div className="stat-label">First activity</div></div>
                  <div className="stat-card"><div className="stat-value" title={analytics.lastActivity}>{analytics.lastActivity.split(' ')[0]}</div><div className="stat-label">Last activity</div></div>
                </div>
              </div>
            )}

            <div className="sidebar-bottom-actions">
              <button
                className={`${canUnarchive ? "btn btn-unarchive-on" : "btn btn-unarchive-off"} sidebar-unarchive`}
                onClick={unarchive}
                disabled={loading || unarchiving || !canUnarchive}
                title={!jobExists ? "Busca un Job primero" : canUnarchive ? "Cambiar status a Pre-Archived" : "Solo habilitado si status actual es Archived, Payed o Billing"}
              >
                {unarchiving && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                Unarchive
              </button>
            </div>
          </aside>

          <main className="history-right">
            <section className="history-top-area glass-panel">
              <div className="history-hero">
                <div className="top-title-row">
                  <div>
                    <h2 className="history-title">Project History</h2>
                    <div className="history-subtitle">Track status changes and project activity in one view.</div>
                  </div>
                  <div className="top-actions">
                    <span className="pill">{latestRowsByJob.length} registro(s)</span>
                    <button className="btn btn-soft" onClick={() => navigate(-1)}>← Back</button>
                  </div>
                </div>
              </div>

              <div className="sf-toolbar">
                <div className="search-input-wrap">
                  <input
                    className="form-control sf-input"
                    placeholder="Job ID"
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setSubmittedQ("");
                      setJobExists(false);
                      setCurrentStatus("");
                      setDetailsOpen(false);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && buscar()}
                  />
                  {q && (
                    <button type="button" className="search-clear" onClick={clearSearch} aria-label="Clear search" title="Clear search">×</button>
                  )}
                </div>
                <button className="btn btn-accent" onClick={buscar} disabled={loading}>
                  {loading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                  {loading ? "Buscando…" : "Buscar"}
                </button>
                <button type="button" className="btn btn-order" onClick={resetTableOrder} title="Default Order · Latest update first" aria-label="Default Order">
                  ↺
                </button>
                <button
                  className={submittedQ.trim() && exactSearchRows.length > 0 ? "btn btn-details-on" : "btn btn-details-off"}
                  onClick={() => setDetailsOpen(true)}
                  disabled={loading || !submittedQ.trim() || exactSearchRows.length === 0}
                >Details</button>
                <div className="current-card">
                  <span className="current-label">Status actual</span>
                  <span className="status-pill">{jobExists ? (currentStatus || "—") : "—"}</span>
                </div>
              </div>
              {error && <div className="alert alert-warning py-2 mt-2 mb-0">{error}</div>}
            </section>

            <section className="history-table-area">
              <div className="table-wrap">
                <div className="table-responsive">
                  <table className="table sf-table table-hover table-sm align-middle mb-0 history-main-table">
                    <thead className="sticky sf-thead">
                      <tr>
                        <th className="marker-head" title="Row marker">#</th>
                        <th className="marker-head" title="Favorite">★</th>
                        {HISTORY_COLUMNS.map((col)=>(
                        <th
                          key={col.key}
                          onClick={() => sortByColumn(col.key)}
                          title={`Sort by ${col.label}`}
                          style={{
                            width: `${COLUMN_WIDTHS[col.key] || 120}px`,
                            minWidth: `${COLUMN_WIDTHS[col.key] || 120}px`,
                            maxWidth: `${COLUMN_WIDTHS[col.key] || 120}px`,
                          }}
                        >
                          {col.label}
                          {sortConfig.key === col.key && <span className="sort-arrow">{sortConfig.direction === "asc" ? "▲" : "▼"}</span>}
                        </th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {latestRowsByJob.length === 0 ? (
                        <tr><td colSpan={HISTORY_COLUMNS.length + 2} className="text-center text-muted py-3">{submittedQ.trim() ? "Job ID Not found...!" : "Sin resultados"}</td></tr>
                      ) : latestRowsByJob.map((r,idx)=>{
                        const id = String(r?.jobid ?? "").trim();
                        const isSel = selectedRows.includes(id);
                        const isFav = favoriteRows.includes(id);
                        const rowNumber = idx + 1;
                        return (
                        <tr key={`${id || "row"}-${idx}`} className={isSel ? "row-selected" : ""}>
                          <td className="sel-cell">
                            <span className="row-index">{rowNumber}</span>
                            <input
                              type="checkbox"
                              className="row-check"
                              checked={isSel}
                              onChange={(e) => handleCheckboxChange(e, id)}
                              title={`Mark row ${rowNumber}`}
                            />
                          </td>
                          <td className="fav-cell">
                            <span
                              className={`fav-star ${isFav ? "on" : "off"}`}
                              onClick={() => toggleFavorite(id)}
                              title={isFav ? "Remove favorite" : "Mark favorite"}
                              role="button"
                              aria-label={isFav ? "Remove favorite" : "Mark favorite"}
                            >{isFav ? "★" : "☆"}</span>
                          </td>
                          {HISTORY_COLUMNS.map((col)=>(
                          <td
                            key={`${idx}-${col.key}`}
                            className={`${col.key === "jobnotesadmin" || col.key === "jobnotes" ? "cell-wrap" : ""}`.trim()}
                            title={String(r?.[col.key] ?? "")}
                            style={{
                              width: `${COLUMN_WIDTHS[col.key] || 120}px`,
                              minWidth: `${COLUMN_WIDTHS[col.key] || 120}px`,
                              maxWidth: `${COLUMN_WIDTHS[col.key] || 120}px`,
                            }}
                          >
                            <div className="main-cell-content">{r?.[col.key] ?? ""}</div>
                          </td>
                        ))}</tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="table-footer">Showing {latestRowsByJob.length} item(s) · Default: 250 · Default Order = latest update first · Header sort uses created_at DESC as secondary · Details shows the complete event history for the searched Job</div>
            </section>
          </main>
        </div>
      </div>

      {detailsOpen && (
        <div className="details-overlay" role="dialog" aria-modal="true" aria-label="Project History Details">
          <div className="details-window">
            <div className="details-head">
              <div>
                <h3 className="details-title">Project History Details</h3>
                <div className="details-subtitle">
                  {submittedQ.trim()} · {exactSearchRows.length} evento(s) · Status actual: {jobExists ? (currentStatus || "—") : "—"}
                </div>
              </div>
              <button className="details-close" onClick={() => setDetailsOpen(false)}>
                Cerrar
              </button>
            </div>

            <div className="details-body">
              <div className="details-table-card">
                <div className="table-responsive">
                  <table className="table sf-table table-hover table-sm align-middle mb-0">
                    <thead className="sticky sf-thead">
                      <tr>
                        <th>jobid</th>
                        <th>jobsta</th>
                        <th>jobuser</th>
                        <th>jobti</th>
                        <th>jobpro</th>
                        <th>his-project</th>
                        <th>jobdatesub</th>
                        <th>Adminnotes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exactSearchRows.map((r, idx) => (
                        <tr key={`detail-${idx}`}>
                          <td>{r?.jobid || ""}</td>
                          <td>{r?.jobsta || ""}</td>
                          <td>{r?.jobuser || ""}</td>
                          <td>{r?.jobti || ""}</td>
                          <td>{r?.jobpro || ""}</td>
                          <td>{r?.["his-project"] || ""}</td>
                          <td>{r?.jobdatesub || ""}</td>
                          <td className="cell-wrap">{r?.jobnotesadmin ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
